import { Button } from '@abyss/web/ui/Button';
import { Checkbox } from '@abyss/web/ui/Checkbox';
import { CheckboxGroup } from '@abyss/web/ui/CheckboxGroup';
import { Flex } from '@abyss/web/ui/Flex';
import { Text } from '@abyss/web/ui/Text';
import isEqual from 'lodash/isEqual';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFilterSearchConfig } from '../../../hooks/useFilterSearchConfig';
import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import { FilterSearchInput } from '../FilterSearchInput';
import { FilterSearchNoResults } from '../FilterSearchNoResults';
import { GroupedCheckboxFilterProps, GroupedItem } from './types';

/**
 * Generic GroupedCheckboxFilter Component
 *
 * Renders a grouped checkbox filter with search functionality.
 * Works with any grouped data structure, not specific to Area of Expertise.
 *
 * Features:
 * - Search across all groups when total items exceed threshold
 * - Keeps group headers visible during search
 * - Pre-selected items pinned at top during search
 * - No-results message
 *
 * @example
 * const groupedData = [
 *   {
 *     category: 'CONTENT_FILTERS.CATEGORY_1',
 *     items: [
 *       { label: 'Option 1', value: 'opt1', dnt_label: 'Option 1' },
 *       { label: 'Option 2', value: 'opt2', dnt_label: 'Option 2' }
 *     ]
 *   }
 * ];
 *
 * <GroupedCheckboxFilter
 *   model="myFilter"
 *   groupedData={groupedData}
 *   onOptionChange={handleChange}
 * />
 */
export const GroupedCheckboxFilter = ({
  model,
  groupedData,
  defaultValue = [],
  isDisabled = false,
  locationForAnalytics,
  onOptionChange = () => {},
  onApply,
  submitBtnName = 'Apply',
  disableSubmitBtn = false,
  showApplyButton = false,
}: GroupedCheckboxFilterProps) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedValues, setSelectedValues] = useState<string[]>(
    defaultValue.map(String)
  );
  // Snapshot of selected items at the time the search term changes.
  // These are shown at the top during search; items selected during search stay in place.
  const preSelectedSnapshot = useRef<string[]>(defaultValue.map(String));
  // Capture the initial default value at mount time for non-search reordering
  const initialDefaultValue = useRef<string[]>(defaultValue.map(String));

  useEffect(() => {
    setSelectedValues(defaultValue.map(String));
  }, [JSON.stringify(defaultValue)]);

  const allItems: GroupedItem[] = groupedData.flatMap((cat) => cat.items);

  // Calculate total items across all groups
  const totalItemsCount = allItems.length;
  const { showSearchInput, getNoResultsMessage } =
    useFilterSearchConfig(totalItemsCount);

  // Update the pre-selected snapshot whenever search term changes
  useEffect(() => {
    if (showSearchInput) {
      preSelectedSnapshot.current = [...selectedValues];
    }
  }, [searchTerm]);

  // Filter groups based on search term, with pre-selected at top
  const filteredGroupedData = useMemo(() => {
    // Keep pre-selected items at the top of each group
    const result: typeof groupedData = [];

    groupedData.forEach((category) => {
      const preSelectedItems: GroupedItem[] = [];
      const restItems: GroupedItem[] = [];

      category.items.forEach((item) => {
        const isPreSelected = showSearchInput
          ? preSelectedSnapshot.current.includes(item.value)
          : initialDefaultValue.current.includes(item.value);

        if (isPreSelected) {
          preSelectedItems.push(item);
        } else {
          restItems.push(item);
        }
      });

      // If searching, filter rest items by search term; pre-selected always visible
      const filteredRest =
        showSearchInput && searchTerm.trim()
          ? restItems.filter((item) =>
              item.label
                .toString()
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
            )
          : restItems;

      // Sort pre-selected alphabetically within the group
      preSelectedItems.sort((a, b) =>
        a.label.toString().localeCompare(b.label.toString())
      );

      const categoryItems = [...preSelectedItems, ...filteredRest];

      if (categoryItems.length > 0) {
        result.push({
          ...category,
          items: categoryItems,
        });
      }
    });

    return result;
  }, [groupedData, searchTerm, showSearchInput]);

  const hasNoResults = searchTerm.trim() && filteredGroupedData.length === 0;

  const handleChange = (
    newCategoryValues: string[],
    categoryItems: GroupedItem[]
  ) => {
    const categoryItemValues = new Set(
      categoryItems.map((item) => item.value).filter(Boolean)
    );
    const keptFromOtherCategories = selectedValues.filter(
      (v) => !categoryItemValues.has(v)
    );
    const nextSelected = [...keptFromOtherCategories, ...newCategoryValues];

    if (isEqual(selectedValues, nextSelected)) return;

    // Adobe analytics
    const added = newCategoryValues.filter((v) => !selectedValues.includes(v));
    const removed = categoryItems
      .filter(
        (item) =>
          item.value &&
          selectedValues.includes(item.value) &&
          !newCategoryValues.includes(item.value)
      )
      .map((item) => item.value);

    if (added.length > 0) {
      const item = categoryItems.find((i) => i.value === added[0]);
      adobeLinkTrackEvent({
        name: `${item?.dnt_label}_${added[0]}:check`,
        location: locationForAnalytics,
        type: 'checkbox',
      });
    } else if (removed.length > 0) {
      const item = categoryItems.find((i) => i.value === removed[0]);
      adobeLinkTrackEvent({
        name: `${item?.dnt_label}_${removed[0]}:uncheck`,
        location: locationForAnalytics,
        type: 'checkbox',
      });
    }

    setSelectedValues(nextSelected);
    onOptionChange({
      [model]: allItems
        .filter((item) => item.value && nextSelected.includes(item.value))
        .map((item) => ({
          label: item.label,
          value: item.value,
          dnt_label: item.dnt_label,
        })),
    });
  };

  return (
    <Flex
      css={{
        'abyss-flex-root': {
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: '16px',
          width: '100%',
        },
      }}
      direction="row"
    >
      {showSearchInput && (
        <FilterSearchInput onChange={setSearchTerm} value={searchTerm} />
      )}

      {hasNoResults ? (
        <FilterSearchNoResults message={getNoResultsMessage(searchTerm)} />
      ) : (
        <Flex
          css={{
            'abyss-flex-root': {
              maxHeight: '400px',
              overflow: 'auto',
              overflowX: 'hidden',
              paddingLeft: '4px',
              paddingBottom: '4px',
              flexDirection: 'row',
              gap: '16px',
            },
          }}
          direction="row"
        >
          {filteredGroupedData.map((category) => {
            const categorySelectedValues = selectedValues.filter((v) =>
              category.items.some((item) => item.value === v)
            );
            const label = category.category.split('.').pop()?.toLowerCase();

            // Use custom test ID prefix if provided (for backward compatibility)
            // Otherwise use generic 'grouped-filter' prefix
            const testIdPrefix =
              category.customTestIdPrefix || 'grouped-filter';

            return (
              <Flex
                css={{
                  'abyss-flex-root': {
                    flexDirection: 'column',
                    gap: '4px',
                    width: '100%',
                  },
                }}
                direction="row"
                key={category.category}
              >
                <Text
                  css={{
                    'abyss-text-root': {
                      fontWeight: '700',
                      fontSize: '14px !important',
                      color: '#323334',
                      paddingBottom: '4px',
                      lineHeight: '18px',
                    },
                  }}
                  data-testid={`${testIdPrefix}-${label}-label`}
                >
                  {t(category.category)}
                </Text>
                <CheckboxGroup
                  css={{
                    'abyss-checkbox-group-label': { margin: '0' },
                    'abyss-checkbox-group-root': { border: 'none' },
                  }}
                  data-testid={`${testIdPrefix}-${category.category
                    .replace(/ /g, '-')
                    .toLowerCase()}-group`}
                  label=""
                  onChange={(e) => handleChange(e, category.items)}
                  validators={{ required: false }}
                  value={categorySelectedValues}
                >
                  {category.items.map((item) => (
                    <Checkbox
                      css={{
                        'abyss-checkbox-root': {
                          padding: '10px 0px 10px $xs',
                          margin: '0 !important',
                          alignItems: 'center !important',
                        },
                        'abyss-checkbox-label': {
                          fontSize: '16px',
                          color: isDisabled
                            ? '$gray5'
                            : selectedValues.includes(item.value)
                            ? '$gray8'
                            : '$gray7',
                          fontWeight:
                            !isDisabled && selectedValues.includes(item.value)
                              ? '$bold'
                              : '$medium',
                        },
                      }}
                      data-testid="checkbox-selection"
                      isDisabled={isDisabled || !item.value}
                      key={item.value || item.label}
                      label={item.label}
                      value={item.value}
                    />
                  ))}
                </CheckboxGroup>
              </Flex>
            );
          })}
        </Flex>
      )}

      {showApplyButton && (
        <Flex
          css={{
            'abyss-flex-root': {
              justifyContent: 'flex-start',
              width: '100%',
            },
          }}
          direction="row"
        >
          <Button
            data-testid={`${model}-grouped-filter-apply`}
            isDisabled={disableSubmitBtn}
            onClick={() => onApply?.(selectedValues)}
          >
            {submitBtnName}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};
